import { useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { Button } from "@/components/ui/button";
import { DatePickerField } from "@/components/ui/date-picker";

export interface DateRange {
  from: Dayjs;
  to: Dayjs;
}

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const presets = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
] as const;

export function getDefaultRange(): DateRange {
  return {
    from: dayjs().subtract(30, "day").startOf("day"),
    to: dayjs().endOf("day"),
  };
}

export function toISO(d: Dayjs): string {
  return d.toISOString();
}

export default function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [activePreset, setActivePreset] = useState<number | null>(30);

  const handlePreset = (days: number) => {
    setActivePreset(days);
    onChange({
      from: dayjs().subtract(days, "day").startOf("day"),
      to: dayjs().endOf("day"),
    });
  };

  const handleFromChange = (date: Date | undefined) => {
    if (date) {
      setActivePreset(null);
      onChange({ ...value, from: dayjs(date).startOf("day") });
    }
  };

  const handleToChange = (date: Date | undefined) => {
    if (date) {
      setActivePreset(null);
      onChange({ ...value, to: dayjs(date).endOf("day") });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex gap-1">
        {presets.map((p) => (
          <Button
            key={p.days}
            variant={activePreset === p.days ? "default" : "outline"}
            size="sm"
            onClick={() => handlePreset(p.days)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <DatePickerField
        value={value.from.toDate()}
        onChange={handleFromChange}
        placeholder="From"
        className="w-[160px]"
      />
      <DatePickerField
        value={value.to.toDate()}
        onChange={handleToChange}
        placeholder="To"
        className="w-[160px]"
      />
    </div>
  );
}
